// @file Vuex Store for Content picker panel and menu
import { isAppUsing } from '@@/bits/flip'
import { asciiSafeStringify } from '@@/bits/json_stringify'
import safeStorage from '@@/bits/safe_storage'
import { vDel, vSet } from '@@/bits/vue'
import {
  aiImageSearch,
  search,
  searchByUrl,
  SearchFilters,
  type AiImageSearchResult,
  type AudioSearchResult,
  type ImageSearchResult,
  type SearchResult,
  type SearchResultDataCollectionMap,
  type VideoSearchResult,
  type WebSearchResult,
} from '@@/bits/web_search'
import type { Module } from 'vuex'

enum ContentPickerSource {
  FileBrowser = 'file',
  LinkInput = 'link',
  WebBrowser = 'web_search',
  PhotoBooth = 'photo_booth',
  VideoRecorder = 'video_recorder',
  AudioRecorder = 'audio_recorder',
  DrawingCanvas = 'drawing',
  PadletBrowser = 'padlet',
  MapBrowser = 'location_search',
  ScreenshareRecorder = 'screen_recorder',
  AiImageSearch = 'ai_image_search',
  ImageSearch = 'image_search',
  VideoSearch = 'video_search',
  AudioSearch = 'audio_search',
  GifSearch = 'gif_search',
  Poll = 'poll',
  GoogleDrive = 'google_drive',
}

interface ContentSourceMenuPosition {
  top: number
  left: number
}

interface ContentPickerState {
  xContentSourceMenu: boolean
  activeContentSource: ContentPickerSource | null
  iconSearchMenuPosition: ContentSourceMenuPosition | null
  isOpenedFromSearch: boolean
  isSearchQueryInProgress: string[]
  isSearchMoreQueryInProgress: string[]
  error: any
  searchResults: Record<string, SearchResultDataCollectionMap>
  searchMoreUrls: Record<string, Record<SearchFilters, string>>
  searchFilter: SearchFilters | null
  defaultSearchMode: SearchFilters
  xSearchScreen: boolean
  xAiImagePreview: boolean
  searchQuery: string
  submittedSearchQuery: string
  searchSuggestions: string[]
  isInformingLastSearchNoResults: boolean
  // for link input, we still show the content picker but with a different placeholder text in the search bar and save the link as post attachment instead of searching
  isLinkInputMode: boolean
  xAiImageResults: boolean
  searchBarPlaceholder: string
}

const blankState = (): ContentPickerState => ({
  xContentSourceMenu: false,
  activeContentSource: null,
  iconSearchMenuPosition: null,
  isOpenedFromSearch: false,
  isSearchQueryInProgress: [],
  isSearchMoreQueryInProgress: [],
  error: null,
  searchResults: {},
  searchMoreUrls: {},
  searchFilter: null,
  defaultSearchMode: SearchFilters.Web,
  xSearchScreen: false,
  xAiImagePreview: false,
  searchQuery: '',
  submittedSearchQuery: '',
  searchSuggestions: [],
  isInformingLastSearchNoResults: false,
  isLinkInputMode: false,
  xAiImageResults: false,
  searchBarPlaceholder: '',
})
const ContentPickerModule: Module<ContentPickerState, any> = {
  namespaced: true,
  state: blankState(),
  getters: {
    xContentPicker: (state, getters): boolean =>
      !!state.activeContentSource || getters.xContentSourceMenu || !!state.xSearchScreen,
    xContentPickerPanel: (state): boolean => !!state.activeContentSource,
    activeContentPickerPanel: (state): ContentPickerSource | null => state.activeContentSource,
    xContentSourceMenu: (state): boolean => state.xContentSourceMenu,
    xSearchScreen: (state): boolean => state.xSearchScreen,
    xAiImagePreview: (state): boolean => state.xAiImagePreview,
    defaultSearchMode: (state) => state.defaultSearchMode,
    searchFilter: (state): SearchFilters | null => state.searchFilter,
    searchQuery: (state): string => state.searchQuery,
    allResults: (state) => state.searchResults,
    xIconImageSearch: (state) => !(state.iconSearchMenuPosition == null),
    searchQueryShowingResult: (state): string => {
      if (!state.searchFilter) return ''
      const searchQuery = state.searchQuery
      const resultForSearchQuery: any[] = (state.searchResults[searchQuery] || {})[state.searchFilter] || []
      if (state.searchQuery == state.submittedSearchQuery) {
        return searchQuery
      }
      if (resultForSearchQuery.length > 0) {
        return searchQuery
      }
      return state.submittedSearchQuery
    },
    currentSearchResultsMap: (state): SearchResultDataCollectionMap => {
      return state.searchResults[state.searchQuery]
    },
    currentSearchResultsForFilter: (
      state,
      getters,
    ): Array<WebSearchResult | AudioSearchResult | VideoSearchResult | ImageSearchResult | AiImageSearchResult> => {
      if (!state.searchFilter) return []
      const allResults = state.searchResults[getters.searchQueryShowingResult]
      return allResults ? allResults[state.searchFilter] || [] : []
    },
    currentSearchMoreUrlForFilter: (state, getters): string => {
      if (!state.searchFilter) return ''
      return (state.searchMoreUrls[getters.searchQueryShowingResult] || {})[state.searchFilter]
    },
    getSearchResults:
      (state) =>
      (query: string): SearchResultDataCollectionMap =>
        state.searchResults[query],
    getSearchMoreUrl:
      (state) =>
      (query: string, filter: SearchFilters): string =>
        state.searchMoreUrls && state.searchMoreUrls[query] && state.searchMoreUrls[query][filter],
    isError: (state) => !!state.error,
    isSearchQueryInProgress: (state) => state.isSearchQueryInProgress.length > 0,
    isSearchMoreQueryInProgress: (state) => state.isSearchMoreQueryInProgress.length > 0,
    isOpenedFromSearch: (state) => state.isOpenedFromSearch,
    searchSuggestions: (state, _getters, _rootState, rootGetters) => {
      return [...new Set(state.searchSuggestions.reverse())]
    },
    xSearchSuggestions: (state, getters) => {
      /**
       * Cases where we show search suggestions:
       * - when user first open the screen and hasn't start a search.
       *    - currentSearchResultsForFilter is empty
       * - when user searched but deleted all the text/press clear button.
       *    - currentSearchResultsForFilter will reset when query becomes empty
       *    - @see SET_SEARCH_QUERY
       * - when user searched a query with no results and changed it immediately
       *    - we utilize isInformingLastSearchNoResults
       ***/
      if (getters.searchSuggestions.length === 0) return false
      if (getters.isSearchQueryInProgress) return false
      if (getters.currentSearchResultsForFilter.length !== 0) return false
      if (state.isInformingLastSearchNoResults) return false
      return true
    },
    isLinkInputMode: (state): boolean => state.isLinkInputMode,
    isAiImageSearchMode: (state): boolean => state.xAiImageResults || state.xAiImagePreview,
    xAiImageResults: (state): boolean => state.xAiImageResults,
    xGoogleDrive: (): boolean => isAppUsing('googleDrive'),
    searchBarPlaceholder: (state): string => state.searchBarPlaceholder,
  },
  mutations: {
    SHOW_ICON_SEARCH_MENU(
      state,
      { position, searchFilter }: { position: ContentSourceMenuPosition; searchFilter: SearchFilters },
    ): void {
      state.iconSearchMenuPosition = position
      state.isOpenedFromSearch = true
      state.xSearchScreen = true
      state.searchFilter = searchFilter
    },
    HIDE_ICON_SEARCH_MENU(state): void {
      state.iconSearchMenuPosition = null
    },
    SHOW_CONTENT_SOURCE_MENU(
      state,
      {
        search = false,
        searchFilter,
      }: {
        search: boolean
        searchFilter: SearchFilters
      },
    ): void {
      state.xContentSourceMenu = true
      if (search) {
        state.isOpenedFromSearch = true
        state.xSearchScreen = true
        state.searchFilter = searchFilter
      }
    },
    HIDE_CONTENT_SOURCE_MENU(state): void {
      state.xContentSourceMenu = false
    },
    PICK_CONTENT_SOURCE(state, source: ContentPickerSource): void {
      state.activeContentSource = source
    },
    HIDE_CONTENT_PICKER_PANEL(state): void {
      state.activeContentSource = null
    },
    UPDATE_SEARCH_RESULT(state, { query, filter, data }: { query: string; filter: SearchFilters; data: any[] }) {
      vSet(state.searchResults, query, {
        ...state.searchResults[query],
        [filter]: data,
      })
    },
    UPDATE_SEARCH_ERROR(state, error) {
      state.error = error
    },
    CLEAR_SEARCH_ERROR(state) {
      state.error = null
    },
    APPEND_SEARCH_RESULT(state, { query, filter, data }: { query: string; filter: SearchFilters; data: any[] }) {
      vSet(state.searchResults, query, {
        ...state.searchResults[query],
        [filter]: [...state.searchResults[query][filter], ...data],
      })
    },
    RECORD_SEARCH_MORE_URL(state, { query, filter, url }) {
      vSet(state.searchMoreUrls, query, { ...state.searchMoreUrls[query], [filter]: url })
    },
    CLEAR_SEARCH_MORE_URL(state, { query, filter }) {
      vSet(state.searchMoreUrls, query, { ...state.searchMoreUrls[query], [filter]: undefined })
    },
    SET_SEARCH_QUERY(state, searchQuery) {
      state.searchQuery = searchQuery
      state.isInformingLastSearchNoResults = false
      if (searchQuery.trim() == '') {
        state.submittedSearchQuery = ''
      }
    },
    SET_SEARCH_SUGGESTIONS(state, suggestions) {
      state.searchSuggestions = suggestions
    },
    PICK_SEARCH_FILTER(state, filter) {
      if (filter && !state.xAiImageResults) {
        state.xSearchScreen = true
      }
      state.submittedSearchQuery = state.searchQuery
      state.searchFilter = filter
      state.error = null
    },
    SET_DEFAULT_SEARCH_MODE(state, mode: SearchFilters) {
      state.defaultSearchMode = mode
    },
    CLEAR_SEARCH_FILTER(state) {
      state.searchFilter = null
    },
    OPEN_SEARCH(state) {
      state.xSearchScreen = true
    },
    CLOSE_SEARCH(state) {
      state.xSearchScreen = false
    },
    OPEN_AI_IMAGE_PREVIEW(state) {
      state.xAiImagePreview = true
    },
    CLOSE_AI_IMAGE_PREVIEW(state) {
      state.xAiImagePreview = false
    },
    START_SEARCH(state, { query, more } = { more: false }) {
      state.submittedSearchQuery = state.searchQuery
      if (more) {
        state.isSearchMoreQueryInProgress = [...state.isSearchMoreQueryInProgress, query]
      } else {
        state.isSearchQueryInProgress = [...state.isSearchQueryInProgress, query]
      }
    },
    FINISH_SEARCH(state, { query }) {
      if (state.isSearchQueryInProgress.includes(query)) {
        vDel(state.isSearchQueryInProgress, state.isSearchQueryInProgress.indexOf(query))
      }
      if (state.isSearchMoreQueryInProgress.includes(query)) {
        vDel(state.isSearchMoreQueryInProgress, state.isSearchMoreQueryInProgress.indexOf(query))
      }
    },
    INFORM_NO_SEARCH_RESULTS(state) {
      state.isInformingLastSearchNoResults = true
    },
    RESET(state) {
      let searchFilter = state.searchFilter
      if (searchFilter === SearchFilters.AiImages) {
        searchFilter = SearchFilters.Images
      }
      Object.assign(state, { ...blankState(), searchFilter })
    },
    START_LINK_INPUT_MODE(state) {
      state.isLinkInputMode = true
    },
    OPEN_AI_IMAGE_RESULTS(state) {
      state.xAiImageResults = true
    },
    CLOSE_AI_IMAGE_RESULTS(state) {
      state.xAiImageResults = false
    },
    END_LINK_INPUT_MODE(state) {
      state.isLinkInputMode = false
    },
    SET_SEARCH_BAR_PLACEHOLDER(state, placeholder) {
      state.searchBarPlaceholder = placeholder
    },
  },
  actions: {
    showIconSearchMenu({ commit, getters }, { position, searchFilter }): void {
      commit('SHOW_ICON_SEARCH_MENU', { position, searchFilter: searchFilter ?? getters.defaultSearchMode })
    },
    hideIconSearchMenu({ commit }): void {
      commit('HIDE_ICON_SEARCH_MENU')
      commit('RESET')
    },
    showContentSourceMenu(
      { commit, getters },
      {
        search,
        searchFilter,
      }: {
        search?: boolean
        searchFilter?: SearchFilters
      } = {},
    ): void {
      commit('SHOW_CONTENT_SOURCE_MENU', { search, searchFilter: searchFilter ?? getters.defaultSearchMode })
    },
    hideContentSourceMenu({ commit }): void {
      commit('HIDE_CONTENT_SOURCE_MENU')
      commit('RESET')
    },
    pickContentSource({ commit, dispatch }, { source }): void {
      dispatch('endLinkInputMode')
      commit('PICK_CONTENT_SOURCE', source)
      commit('HIDE_CONTENT_SOURCE_MENU')
    },
    hideContentPickerPanel({ commit }): void {
      commit('HIDE_CONTENT_PICKER_PANEL')
      commit('RESET')
    },
    pickSearchFilter({ commit, dispatch }, { filter }: { filter: SearchFilters }): void {
      if (filter !== SearchFilters.AiImages) {
        dispatch('endLinkInputMode')
      }
      commit('PICK_SEARCH_FILTER', filter)
    },
    setDefaultSearchMode({ commit }, mode: SearchFilters): void {
      commit('SET_DEFAULT_SEARCH_MODE', mode)
    },
    openSearch({ commit }): void {
      commit('OPEN_SEARCH')
    },
    closeSearch({ commit }): void {
      commit('CLOSE_SEARCH')
    },
    clearSearchFilter({ commit }): void {
      commit('CLEAR_SEARCH_FILTER', null)
    },
    setSearchQuery({ commit }, { query }): void {
      commit('SET_SEARCH_QUERY', query)
    },
    appendSearchSuggestions({ commit }, { query }): void {
      const stringValue = safeStorage.getItem('contentPickerSearchSuggestions')
      const currentSuggestions: string[] = stringValue ? JSON.parse(stringValue) : []
      let newSuggestions = currentSuggestions.filter((q) => q !== query)
      newSuggestions.push(query)
      newSuggestions = newSuggestions.slice(-10)
      safeStorage.setItem('contentPickerSearchSuggestions', asciiSafeStringify(newSuggestions))
      commit('SET_SEARCH_SUGGESTIONS', newSuggestions)
    },
    syncSearchSuggestionsWithStorage({ commit }): void {
      const stringValue = safeStorage.getItem('contentPickerSearchSuggestions')
      const suggestions: string[] = stringValue ? JSON.parse(stringValue) : []
      commit('SET_SEARCH_SUGGESTIONS', suggestions)
    },
    handleSearchResult(
      { commit },
      {
        result,
        filter,
        query,
        mode = 'update',
      }: { result: SearchResult; filter; query: string; mode: 'update' | 'append' },
    ): void {
      const mutationType = mode == 'update' ? 'UPDATE_SEARCH_RESULT' : 'APPEND_SEARCH_RESULT'
      let searchHits: any[] = []
      switch (filter) {
        case SearchFilters.Web:
          searchHits = result.data.webpages as WebSearchResult[]
          break
        case SearchFilters.Audio:
          searchHits = result.data.audio as AudioSearchResult[]
          break
        case SearchFilters.Videos:
          searchHits = result.data.videos as VideoSearchResult[]
          break
        case SearchFilters.Images:
        case SearchFilters.Gif:
          searchHits = result.data.images as ImageSearchResult[]
          break
        case SearchFilters.Stock:
          // not used anywhere
          break
        case SearchFilters.AiImages:
          searchHits = result.data.images as AiImageSearchResult[]
      }
      if (searchHits.length === 0) commit('INFORM_NO_SEARCH_RESULTS')
      commit(mutationType, { query, filter, data: searchHits })
      if (result.links?.next && searchHits.length > 0) {
        commit('RECORD_SEARCH_MORE_URL', {
          query,
          filter,
          url: result.links.next,
        })
      } else {
        commit('CLEAR_SEARCH_MORE_URL', {
          query,
          filter,
        })
      }
    },
    handleSearchError({ commit, state }, { filter, error }): void {
      if (filter == state.searchFilter) {
        commit('UPDATE_SEARCH_ERROR', error)
      } else {
        // do nothing, error belongs to the different tab.
      }
    },
    clearSearchError({ commit }): void {
      commit('CLEAR_SEARCH_ERROR')
    },
    async search(
      { commit, state, getters, dispatch, rootState },
      options: {
        filter: SearchFilters
        query?: string
      },
    ): Promise<void> {
      let searchResult: SearchResult | null = null
      if (state.searchFilter == null) {
        void dispatch('pickSearchFilter', { filter: getters.defaultSearchMode })
      }
      const filter: SearchFilters = options?.filter ?? state.searchFilter ?? getters.defaultSearchMode
      const query: string = options?.query || state.searchQuery
      dispatch('appendSearchSuggestions', { query })
      try {
        commit('START_SEARCH', { query })

        if (filter === SearchFilters.AiImages) {
          // We have different logic for AI images as it hits the endpoint directly
          // without going through the beethoven search API
          dispatch('startAiImageSearch')
          searchResult = await aiImageSearch(query, rootState?.user?.id)
          if (searchResult.error) {
            throw new Error(searchResult.error)
          }
        } else {
          searchResult = await search(query, rootState?.user?.id, filter)
        }
        if (searchResult != null) {
          dispatch('handleSearchResult', { result: searchResult, filter, query, mode: 'update' })
        }
      } catch (error) {
        dispatch('handleSearchError', { filter, error })
      } finally {
        commit('FINISH_SEARCH', { query })
      }
    },
    async searchMore({ commit, state, getters, dispatch }, options: { query: string }) {
      let searchResult: SearchResult | null = null
      const filter: SearchFilters = state.searchFilter ?? getters.defaultSearchMode
      const query: string = options?.query || state.searchQuery
      try {
        commit('START_SEARCH', { query, more: true })
        const currentFilter: SearchFilters = filter
        const url = state.searchMoreUrls[query][currentFilter]
        if (url) searchResult = await searchByUrl(url) // For searching using next url
        if (searchResult != null)
          dispatch('handleSearchResult', { result: searchResult, filter, query, mode: 'append' })
      } catch (error) {
        dispatch('handleSearchError', { error })
      } finally {
        commit('FINISH_SEARCH', { query })
      }
    },
    reset({ commit }): void {
      commit('RESET')
    },
    startLinkInputMode({ commit }): void {
      commit('START_LINK_INPUT_MODE')
    },
    endLinkInputMode({ commit }): void {
      commit('END_LINK_INPUT_MODE')
    },
    openAiImagePreview({ commit }): void {
      commit('OPEN_AI_IMAGE_PREVIEW')
    },
    reopenAiImagePreview({ commit }): void {
      commit('CLOSE_AI_IMAGE_RESULTS')
      commit('OPEN_AI_IMAGE_PREVIEW')
    },
    startAiImageSearch({ commit }): void {
      commit('CLOSE_AI_IMAGE_PREVIEW')
      commit('OPEN_AI_IMAGE_RESULTS')
    },
    closeAiImageSearchMode({ commit }): void {
      commit('CLOSE_AI_IMAGE_PREVIEW')
      commit('CLOSE_AI_IMAGE_RESULTS')
    },
    setSearchBarPlaceholder({ commit }, placeholder): void {
      commit('SET_SEARCH_BAR_PLACEHOLDER', placeholder)
    },
  },
}

export default ContentPickerModule
export { ContentPickerSource, type ContentPickerState, type ContentSourceMenuPosition }
